import { useMediaQuery } from "@mui/material"
import React, { useEffect, useState } from "react"
import styled from "styled-components"
import { Link } from "gatsby"
import { Button } from "../ui/Button"
import { useCheckoutState } from "../../checkout/state"
import theme from "../../material-ui/theme"
import { formatCurrencyString } from "../../utils/format"
import { isSubscriptionInCart } from "../../checkout/shared"
import { Currency } from "../../checkout/types/checkoutTypes"
import { StrapiMembership } from "../../hooks/useMembershipConfig"
import { findProduct, useProducts } from "../../hooks/useProducts"
import { Product } from "../../checkout/product"

const Box = styled.div<{
  checked: boolean
  isMobile: boolean
  hasBullets: boolean
  isPromo: boolean
}>`
  border-radius: 20px;
  border: ${props =>
    props.checked ? "4px solid #1d99b8" : "2px solid #A7A9AC"};
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  padding: ${props =>
    props.isMobile || !props.hasBullets ? "15px 20px" : "15px 1rem"};
  padding-bottom: 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: ${props =>
    props.isMobile && !props.isPromo ? "300px" : "350px"};
  position: relative;
`

const TitleContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 100%;
  margin-bottom: 16px;
`

const Title = styled.h2<{ isMobile: boolean }>`
  text-align: center;
  font-family: Rubik;
  font-size: ${isMobile => (isMobile ? "24px" : "26px")};
  font-style: normal;
  font-weight: 600;
  line-height: 28px; /* 107.692% */
  letter-spacing: 0.26px;

  background: linear-gradient(90deg, #1d99b8 0%, #5c38bd 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`

const PeriodPrice = styled.div`
  color: #454545;
  text-align: center;
  font-family: Rubik;
  font-size: 26px;
  font-style: normal;
  font-weight: 500;
  line-height: 28px; /* 107.692% */
  letter-spacing: 0.26px;
  margin: 1.5rem;
`

const Dollar = styled.span<{ isMobile: boolean }>`
  color: #454545;
  font-family: Rubik;
  font-size: ${props => (props.isMobile ? "30px" : "32px")};
  font-style: normal;
  font-weight: 500;
  line-height: 28px;
  letter-spacing: 0.32px;
`

const Amount = styled.span<{ isMobile: boolean }>`
  color: #454545;
  font-family: Rubik;
  font-size: ${props => (props.isMobile ? "56px" : "60px")};
  font-style: normal;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: 0.64px;
`

const AmountTotal = styled.div<{ isMobile: boolean }>`
  color: #666666;
  font-family: Rubik;
  font-size: ${props => (props.isMobile ? "14px" : "16px")};
  font-style: normal;
  font-weight: 500;
  line-height: 28px;
  letter-spacing: 0.24px;
  margin-top: -1.25rem;
  margin-bottom: 1rem;
  min-height: ${props => (props.isMobile ? "0" : "28px")};
`

const FooterText = styled.div<{ isMobile: boolean }>`
  color: #666666;
  font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.24px;
  margin-top: -1.25rem;
  margin-bottom: 1rem;
  min-height: ${props => (props.isMobile ? "0" : "28px")};
  position: absolute;
  bottom: -0.5rem;
  padding: 0 2em;
`

const Bullets = styled.div`
  width: 100%;
  justify-content: flex-start;
  margin-bottom: 14px;
`

const Bullet = styled.div`
  padding: 0 5px;
`

const BulletText = styled.span<{ isMobile: boolean }>`
  color: #454545;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.2px;
`

const Emphasized = styled.div<{ isMobile: boolean }>`
  color: #b632b0;
  text-align: center;
  font-family: Rubik;
  font-size: ${props => (props.isMobile ? "16px" : "18px")};
  font-style: normal;
  font-weight: 600;
  line-height: 39px; /* 177.273% */
  letter-spacing: 0.22px;
`

const ButtonContainer = styled.div<{ isPromo: boolean }>`
  display: flex;
  justify-content: center;
  margin-top: auto;
  margin-bottom: 1px;
`

const TextContainer = styled.div<{ isMobile: boolean }>`
  margin: 0 1rem;
  text-align: center;
`

type Props = {
  membership: StrapiMembership
  isPromo: boolean
  personalMembershipLimit: boolean
  professionalMembershipLimit: boolean
  disabled: boolean
  hasProOrg: boolean
  key: string
  handleBuySubscription: (membership: StrapiMembership) => void
}

const MembershipSectionCard = ({
  membership,
  isPromo,
  personalMembershipLimit,
  professionalMembershipLimit,
  hasProOrg,
  disabled,
  key,
  handleBuySubscription,
}: Props) => {
  const checkoutState = useCheckoutState()
  const { cartItems, currency } = checkoutState

  const [showCartLimit, setCartLimit] = useState<boolean>(
    isSubscriptionInCart(cartItems)
  )

  useEffect(() => {
    setCartLimit(isSubscriptionInCart(cartItems))
  }, [cartItems])

  const product =
    membership.type === "product" ? membership.product ?? null : null

  const isMobile = useMediaQuery(theme.breakpoints.down("lg"))

  const products = useProducts()

  const getPricePerMonth = (
    product: Product,
    currency: Currency,
    additionalProduct?: Product | null
  ) => {
    let amt = product.getPriceAmt(currency, true, true) ?? 0
    if (additionalProduct) {
      amt += additionalProduct.getPriceAmt(currency, false, true) ?? 0
    }
    const dividor = product.isYearly() ? 12 : 1

    return formatCurrencyString(
      { value: amt / dividor, currency: currency.toUpperCase() },
      false
    )
  }

  const getAnnualPricePerYear = (product: Product, currency: Currency) => {
    const amt = product.getPriceAmt(currency, true, true) ?? 0
    return formatCurrencyString(
      { value: amt, currency: currency.toUpperCase() },
      false
    )
  }

  const cardProductBundled = membership.additionalProduct
    ? findProduct(products, membership.additionalProduct.id)
    : undefined

  let priceText = "0"
  let priceSubText = ""
  if (product) {
    priceText = getPricePerMonth(product, currency, cardProductBundled)

    if (product?.isYearly()) {
      priceSubText = `($${getAnnualPricePerYear(
        product,
        currency
      )} billed yearly)`
    } else if (product?.isMonthly()) {
      if (product?.hasUpfrontNumCycles()) {
        priceSubText =
          (!isMobile ? "then " : "") +
          product?.getProductBillingDescription({
            currency,
            fullDescription: isMobile,
            couponDiscountDays: 0,
            trialDays: 0,
          })
      }
    }
  }
  if (membership.priceSubTextOverride) {
    priceSubText = membership.priceSubTextOverride
  }

  const isAnnual = product?.isYearly() ?? false
  const bundledProductInCart = product?.getForcedProductBundleId()
    ? findProduct(products, product?.getForcedProductBundleId()!)
    : null

  const hasBullets =
    !!membership.bulletPoints || bundledProductInCart?.isUsage()

  const internalFreeLink =
    membership.type === "freeAccount" || membership.type === "freeApp"

  const renderActiveMember = (isPro = false) => (
    <TextContainer isMobile={isMobile}>
      <div style={{ textAlign: "center" }}>
        <p>
          You have an active {isPro ? "professional " : ""}membership, please{" "}
          <Link to="/user">see your account</Link> for more details.
        </p>
      </div>
    </TextContainer>
  )
  const renderMembershipInCart = () => (
    <TextContainer isMobile={isMobile}>
      <div style={{ textAlign: "center" }}>
        <p>
          You already have a membership in your cart. {` `}
          <Link to="/store/checkout">View your cart.</Link>
        </p>
      </div>
    </TextContainer>
  )

  return (
    <Box
      key={key}
      checked={membership.highlight ?? false}
      isMobile={isMobile}
      hasBullets={hasBullets}
      isPromo={isPromo}
    >
      <Emphasized isMobile={isMobile}>
        {membership.featuredText ? membership.featuredText : <>&emsp;</>}
      </Emphasized>

      <PeriodPrice>
        <Dollar isMobile>$</Dollar>
        <Amount isMobile={isMobile}>{priceText}</Amount>
        {product?.isRecurring() && !product?.hasUpfrontNumCycles() && "/month"}
        {product?.isRecurring() && product?.hasUpfrontNumCycles() && " today"}
      </PeriodPrice>
      <AmountTotal isMobile={isMobile}> {priceSubText}</AmountTotal>
      <TitleContainer isMobile={isMobile}>
        <Title isMobile={isMobile}>{membership.title || product?.name}</Title>
      </TitleContainer>

      {hasBullets && (
        <Bullets>
          {membership.bulletPoints.map((bullet, i) => (
            <Bullet key={i}>
              <BulletText isMobile={isMobile}>
                {bundledProductInCart
                  ? bundledProductInCart.replaceUsageDescription(
                      bullet.text ?? bullet.text,
                      currency
                    )
                  : bullet.text}
              </BulletText>
            </Bullet>
          ))}
        </Bullets>
      )}
      <ButtonContainer isPromo={isPromo || isMobile}>
        {(!product?.isRecurring() ||
          (!(product?.isPersonalMembership() && personalMembershipLimit) &&
            !(
              product?.isProfessionalMembership() && professionalMembershipLimit
            ) &&
            !showCartLimit)) && (
          <Button
            type={membership.highlight ?? false ? "primary" : "primaryOutline"}
            title={
              membership.product?.isPro && !hasProOrg
                ? "GET STARTED"
                : membership.buttonText ?? "ADD TO CART"
            }
            sectionId="membership"
            id={
              membership.type === "product" && membership.product?.isPro
                ? "continue_pro_signup"
                : membership.type === "freeAccount"
                ? "continue_free_signup"
                : membership.type === "freeApp"
                ? "continue_free_app"
                : isAnnual
                ? "continue_yearly"
                : "continue_monthly"
            }
            link={
              internalFreeLink
                ? [
                    {
                      url: membership.type === "freeApp" ? "/app" : "/signup",
                    },
                  ]
                : undefined
            }
            onClick={() => {
              if (internalFreeLink) {
                return
              }

              handleBuySubscription(membership)
            }}
            disabled={disabled}
          />
        )}
        {personalMembershipLimit &&
          !showCartLimit &&
          product?.isPersonalMembership() &&
          renderActiveMember()}
        {professionalMembershipLimit &&
          !showCartLimit &&
          product?.isProfessionalMembership() &&
          renderActiveMember(true)}
        {showCartLimit && product?.isRecurring() && renderMembershipInCart()}
      </ButtonContainer>
      {membership.footerText && (
        <FooterText isMobile={isMobile}>
          {membership.linkFooterToHeadset && (
            <Link to="/store/headset">{membership.footerText}</Link>
          )}
          {!membership.linkFooterToHeadset && membership.footerText}
        </FooterText>
      )}
    </Box>
  )
}

export default MembershipSectionCard
